import {
    useState
} from "react";
import {
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    PageContainer,
    TextInput,
    Button,
    Text
} from "ncore-ui-kit";
import {
    type RouteProp,
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../navigation/type";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import md5 from "md5";
import {
    SERVER_ADDRESS
} from "../../services/restAPI/constants";

const ResetPassword = () => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const route = useRoute<RouteProp<RootStackParamList, "ResetPassword">>();
    const passedEmail = route.params?.email || "";
    const passedToken = route.params?.token || "";

    const [
        code,
        setCode
    ] = useState("");
    const [
        newPassword,
        setNewPassword
    ] = useState("");
    const [
        newPasswordConfirm,
        setNewPasswordConfirm
    ] = useState("");

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const handleReset = async () => {
        if (!code || !newPassword || !newPasswordConfirm) {
            NCoreUIKitToast.open({
                title: localize("reset-password-alert-fields-required")
            });
            return;
        }

        if (newPassword !== newPasswordConfirm) {
            NCoreUIKitToast.open({
                title: localize("reset-password-alert-password-mismatch")
            });
            return;
        }

        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const checkResponse = await fetch(`${SERVER_ADDRESS}/auth/checkForgotPWCode`, {
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    token: passedToken,
                    mail: passedEmail,
                    code
                }),
                method: "POST"
            });
            const checkData = await checkResponse.json();

            if (checkData.code !== 200 && checkData.code !== 201) {
                NCoreUIKitToast.open({
                    title: checkData.message || localize("reset-password-toast-failed")
                });
                setIsSubmitting(false);
                return;
            }

            const token = checkData.payload?.token;

            if (!token) {
                NCoreUIKitToast.open({
                    title: localize("reset-password-toast-failed")
                });
                setIsSubmitting(false);
                return;
            }

            const setResponse = await fetch(`${SERVER_ADDRESS}/auth/setForgottenPassword`, {
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    newPassword: md5(newPassword),
                    mail: passedEmail,
                    token
                }),
                method: "POST"
            });
            const setData = await setResponse.json();

            if (setData.code === 200 || setData.code === 201) {
                NCoreUIKitToast.open({
                    title: setData.message || localize("reset-password-toast-success")
                });
                navigation.navigate("Login");
            } else {
                NCoreUIKitToast.open({
                    title: setData.message || localize("reset-password-toast-failed")
                });
            }
        } catch {
            NCoreUIKitToast.open({
                title: localize("reset-password-toast-network-error")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return <PageContainer
        scrollViewProps={{
            contentContainerStyle: stylesheet.scrollContentContainer,
            showsHorizontalScrollIndicator: false,
            showsVerticalScrollIndicator: false
        }}
        scrollViewStyle={{
            backgroundColor: colors.project.inputBackground
        }}
        isCustomPadding={true}
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <Text
                variant="titleLargeSize"
            >
                {localize("reset-password-page-title")}
            </Text>
            <Text
                style={[
                    stylesheet.subtitle,
                    {
                        marginBottom: spaces.spacingXl,
                        marginTop: spaces.spacingMd
                    }
                ]}
                variant="bodyLargeSize"
            >
                {localize("reset-password-page-subtitle")}
            </Text>
            <View
                style={stylesheet.formContainer}
            >
                <TextInput
                    placeholder={localize("reset-password-code-placeholder")}
                    customColor={colors.project.brandWhite}
                    spreadBehaviour="stretch"
                    onChangeText={setCode}
                    value={code}
                />
                <View
                    style={{
                        height: spaces.spacingMd
                    }}
                />
                <TextInput
                    placeholder={localize("reset-password-new-password-placeholder")}
                    customColor={colors.project.brandWhite}
                    onChangeText={setNewPassword}
                    spreadBehaviour="stretch"
                    value={newPassword}
                    variant="hidden"
                    secureTextEntry
                />
                <View
                    style={{
                        height: spaces.spacingMd
                    }}
                />
                <TextInput
                    placeholder={localize("reset-password-new-password-confirm-placeholder")}
                    customColor={colors.project.brandWhite}
                    onChangeText={setNewPasswordConfirm}
                    value={newPasswordConfirm}
                    spreadBehaviour="stretch"
                    variant="hidden"
                    secureTextEntry
                />
                <View
                    style={{
                        height: spaces.spacingLg
                    }}
                />
                <Button
                    title={localize("reset-password-submit-button")}
                    spreadBehaviour="stretch"
                    isLoading={isSubmitting}
                    onPress={handleReset}
                />
                <View
                    style={[
                        stylesheet.backButtonContainer,
                        {
                            marginTop: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        title={localize("reset-password-back-to-login")}
                        onPress={() => navigation.navigate("Login")}
                        customBorderColor="secondaryButton"
                        customColor="secondaryButton"
                        spreadBehaviour="stretch"
                        variant="filled"
                    />
                </View>
            </View>
        </View>
    </PageContainer>;
};

export default ResetPassword;
